import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { Bell } from 'lucide-react';
import axiosClient from '../api/axiosClient';

const SocketContext = createContext();

export function useSocket() {
  return useContext(SocketContext);
}

export function SocketProvider({ children, userType, email }) {
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    if (!userType) return;

    let joinedRoom = email;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';
    const socketUrl = apiUrl.replace(/\/api$/, '');
    const newSocket = io(socketUrl);
    setSocket(newSocket);

    const initConnection = async () => {
      if (userType === 'client' && !joinedRoom) {
        try {
          const res = await axiosClient.get('/trials/me');
          const data = res.data;
          if (data.profile?.email) {
            joinedRoom = data.profile.email;
          }
        } catch (e) {
          console.error('Failed to get client email for socket room', e);
        }
      }
      
      newSocket.on('connect', () => {
        const finalRoom = userType === 'admin' ? 'admin' : joinedRoom;
        if (finalRoom) {
          newSocket.emit('join', finalRoom);
        }
      });
    };

    initConnection();

    newSocket.on('notification', (notif) => {
      // Trigger a toast popup on new notification
      toast(
        <div className="flex flex-col gap-1">
          <span className="font-bold text-sm text-slate-800">{notif.type.replace(/_/g, ' ').toUpperCase()}</span>
          <span className="text-xs text-slate-600">{notif.message}</span>
        </div>,
        {
          icon: <Bell className="text-primary-500" size={20} />,
          duration: 5000,
          position: 'top-right',
          style: {
            border: '1px solid #e2e8f0',
            padding: '16px',
            color: '#1e293b',
          },
        }
      );
      
      // Dispatch a custom event so UI components (like navbars) can refetch notifications
      window.dispatchEvent(new CustomEvent('new-notification', { detail: notif }));
    });

    return () => newSocket.close();
  }, [userType, email]);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
}
